/**
 * OTP API Service
 * Handles mobile number verification via OTP
 */

import { apiClient } from './client';

export interface OtpGenerateRequest {
  captchaToken?: string; // Optional for backward compatibility
  countryCode: string;
  mobileNumber: string;
  email: string;
}

export interface OtpGenerateResponse {
  success: boolean;
  message: string;
}

export interface OtpValidateRequest {
  countryCode: string;
  mobileNumber: string;
  otp: string;
}

export interface OtpValidateResponse {
  success: boolean;
  message: string;
}

/**
 * Generate and send OTP to mobile number
 */
export const generateOtp = async (
  request: OtpGenerateRequest
): Promise<OtpGenerateResponse> => {
  console.log('📡 [OtpService] ====== OTP GENERATION START ======');
  console.log('📡 [OtpService] Request details:', {
    email: request.email,
    countryCode: request.countryCode,
    mobileNumber: request.mobileNumber.replace(/\d(?=\d{4})/g, '*'),
    hasCaptchaToken: !!request.captchaToken,
    captchaTokenLength: request.captchaToken?.length || 0,
    captchaTokenPreview: request.captchaToken ? request.captchaToken.substring(0, 20) + '...' : 'N/A',
    timestamp: new Date().toISOString()
  });

  // 🚨 COMPLETE API PAYLOAD LOGGING
  console.log('📋 [OtpService] COMPLETE API PAYLOAD for /api/otp/generate:');
  console.log('📋 [OtpService] URL:', '/api/otp/generate');
  console.log('📋 [OtpService] Method:', 'POST');
  console.log('📋 [OtpService] Headers:', {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  });
  console.log('📋 [OtpService] Full Request Body (JSON):');
  console.log(JSON.stringify(request, null, 2));

  // Also log as curl command for easy testing
  console.log('📋 [OtpService] CURL Command Equivalent:');
  const curlCommand = `curl -X POST http://localhost:8080/api/otp/generate \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '${JSON.stringify(request, null, 2)}'`;
  console.log(curlCommand);

  try {
    console.log('📡 [OtpService] Making API call to /api/otp/generate...');
    const response = await apiClient.post<OtpGenerateResponse>(
      '/api/otp/generate',
      request
    );

    console.log('📡 [OtpService] API Response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      timestamp: new Date().toISOString()
    });

    console.log('✅ [OtpService] ====== OTP GENERATION SUCCESS ======');
    return response.data;
  } catch (error: any) {
    console.error('❌ [OtpService] ====== OTP GENERATION ERROR ======');
    console.error('❌ [OtpService] Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });

    // Log the exact request that failed
    console.error('❌ [OtpService] Failed request payload:');
    console.error(JSON.stringify(request, null, 2));

    // Extract error message from response
    const errorMessage =
      error.response?.data?.message || 'Failed to generate OTP. Please try again.';

    console.error('❌ [OtpService] Throwing error:', errorMessage);
    throw new Error(errorMessage);
  }
};

/**
 * Validate OTP entered by user
 */
export const validateOtp = async (
  request: OtpValidateRequest
): Promise<OtpValidateResponse> => {
  console.log('🔐 [OtpService] ====== OTP VALIDATION START ======');
  console.log('🔐 [OtpService] Request details:', {
    countryCode: request.countryCode,
    mobileNumber: request.mobileNumber.replace(/\d(?=\d{4})/g, '*'),
    otpLength: request.otp.length,
    timestamp: new Date().toISOString()
  });

  try {
    console.log('📡 [OtpService] Making API call to /api/otp/validate...');
    const response = await apiClient.post<OtpValidateResponse>(
      '/api/otp/validate',
      request
    );

    console.log('📡 [OtpService] API Response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      timestamp: new Date().toISOString()
    });

    console.log('✅ [OtpService] ====== OTP VALIDATION SUCCESS ======');
    return response.data;
  } catch (error: any) {
    console.error('❌ [OtpService] ====== OTP VALIDATION ERROR ======');
    console.error('❌ [OtpService] Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      stack: error.stack,
      timestamp: new Date().toISOString()
    });

    // Extract error message from response
    const errorMessage =
      error.response?.data?.message || 'Invalid OTP. Please try again.';

    console.error('❌ [OtpService] Throwing error:', errorMessage);
    throw new Error(errorMessage);
  }
};
